import type { AgenticLogicPublishGateKey } from "@intelligems/ig-types/dtos/agentic-logic/publish";
import type {
  AgenticLogicRuleDto,
  AgenticLogicRuleSettings,
} from "@intelligems/ig-types/dtos/agentic-logic/rules";
import { IgAlert } from "@intelligems/ig-theme/IgAlert";
import { IgLoading } from "@intelligems/ig-theme/IgLoading";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import type { AgenticLogicDataSurface } from "@intelligems/ig-utils/agentic-logic-code-snippets";
import { Box, Stack } from "@mui/material";
import { useImmer } from "use-immer";

import { useUpdateAgenticLogicRule } from "@src/api/agentic-logic/update-agentic-logic-rule";
import { useIsAdmin } from "@src/api/user-auths/is-admin";
import { useIsAdvancedAgenticLogicEnabled } from "@src/hooks/feature-flags/useIsAdvancedAgenticLogicEnabled";

import { RuleSettingsFields } from "./RuleSettingsFields";
import { useCancelRuleChecks } from "./useCancelRuleChecks";
import { useSaveRuleSettings } from "./useSaveRuleSettings";
import { sameRuleSettings } from "./ruleSettings";
import { useUnpublishedRuleChanges } from "./useUnpublishedRuleChanges";
import { CodegenWorkspace } from "@src/features/agenticCodegen/workspace/CodegenWorkspace";
import {
  GateProgressStrip,
  type GateStripOperation,
} from "@src/features/agenticCodegen/workspace/components/02-GateRail/GateProgressStrip";
import { RepairApprovalPanel } from "./RepairApprovalPanel";
import { RepairGiveUpPanel } from "./RepairGiveUpPanel";
import { RecentRunsPanel } from "./recent-runs/RecentRunsPanel";
import { RuntimeFactsList } from "@src/features/agenticCodegen/workspace/components/ControlsPanel/ControlsPanel";

import { useAgenticLogicDataSurface } from "../useAgenticLogicDataSurface";
import { RegenerateGraphButton } from "../graph/RegenerateGraphButton";
import { SelectionLogicTab } from "../graph/SelectionLogicTab";
import { useRegenerateGraph } from "../graph/useRegenerateGraph";
import { useEnsureRuleScenarios } from "../graph/useEnsureRuleScenarios";
import { useOrgExclusionsGraph } from "../graph/useOrgExclusionsGraph";
import { useRuleSelectionGraph } from "../graph/useRuleSelectionGraph";
import { useScenarioPreview } from "../graph/useScenarioPreview";
import { RuleDetailPageView } from "./RuleDetailPageView";
import {
  BUSY_HINT,
  isChecksOnlyRun,
  type RuleOperationPhase,
} from "./ruleSaveApplyState";
import { useRuleSaveApplyFlow } from "./useRuleSaveApplyFlow";
import { agenticDraftSource } from "./agenticApiPort";
import { priorRepairAttempts } from "./priorRepairAttempts";
import {
  canOfferRepairBaselineRestore,
  didRepairStop,
  isRepairAwaitingApproval,
  publishedJobCoversCurrentDraft,
  repairStoppedGateReason,
  ruleCheckPhase,
  shouldShowRepairGiveUp,
} from "./repairOutcome";
import { useRuleDetailByline } from "./useRuleDetailByline";
import {
  RULE_SAVE_BLOCK_HINT,
  ruleSaveBlockReason,
} from "./ruleSaveAvailability";
import { DEFAULT_RULE_DETAIL_TAB } from "./ruleDetailTabs";
import { useRuleDetailTab } from "./useRuleDetailTab";
import {
  AGENTIC_CAPABILITIES,
  AGENTIC_GATES_CONFIG,
  AGENTIC_TEST_FILES,
  agenticPublishReviewConfig,
  agenticRollbackImpactCopy,
  buildAgenticDomainConfig,
} from "./agenticDomain";
import { useAgenticLogicRuleWorkspace } from "./useAgenticLogicRuleWorkspace";
import { useDraftGraphPoll } from "./useDraftGraphPoll";
import { useAgenticLogicSurface } from "@src/features/ai/pages/agentic-logic/surface/useAgenticLogicSurface";
import { useSelector } from "@src/store";
import {
  getAtlasBuildingRuleId,
  getIsAtlasCreationSessionActive,
} from "@src/store/slices/global";

/**
 * The Agentic Logic rule workspace: the shared {@link CodegenWorkspace} shell
 * bound to this domain and rendered **inline on a route**. CSP wraps the same
 * shell in a full-page dialog; that chrome is CSP's, not the shell's.
 */
const RuleWorkspace = ({
  rule,
  dataSurface,
}: {
  rule: AgenticLogicRuleDto;
  dataSurface: AgenticLogicDataSurface;
}) => {
  const ruleId = rule.id;
  const surface = useAgenticLogicSurface();
  const isAdmin = useIsAdmin() === true;
  const advancedEnabled = useIsAdvancedAgenticLogicEnabled();
  const vm = useAgenticLogicRuleWorkspace(ruleId, dataSurface);
  const updateRule = useUpdateAgenticLogicRule();
  const saveSettings = useSaveRuleSettings(ruleId);
  const [settingsEdit, setSettingsEdit] = useImmer<{
    base: AgenticLogicRuleSettings;
    value: AgenticLogicRuleSettings;
  } | null>(null);
  const settings = settingsEdit?.value ?? rule.settings;
  const settingsDirty = !sameRuleSettings(settings, rule.settings);
  const settingsConflict =
    settingsDirty &&
    settingsEdit !== null &&
    !sameRuleSettings(settingsEdit.base, rule.settings);
  const isDirty = vm.isDirty || settingsDirty;
  // Saving never changes the applied rule, regardless of placement usage.
  const saveAction = "save_draft_only" as const;
  const comparison = useUnpublishedRuleChanges(rule);
  const { hasUnpublishedChanges, isChecking, comparisonFailed } = comparison;
  const [selectedPageTab, setSelectedPageTab] = useRuleDetailTab(
    DEFAULT_RULE_DETAIL_TAB,
    advancedEnabled,
    surface.tabParam
  );

  const byline = useRuleDetailByline(rule);
  const copySource = agenticDraftSource(vm.draftSnapshot.files);
  const copyDraft =
    copySource !== null
      ? {
          source: copySource,
          userTestSuite: vm.draftSnapshot.userTestSuite,
          dependencies: vm.draftSnapshot.dependencies,
          ...(vm.draftSnapshot.generatedTestSuite === null
            ? {}
            : { generatedTestSuite: vm.draftSnapshot.generatedTestSuite }),
          ...(vm.draftSnapshot.origin === null
            ? {}
            : { origin: vm.draftSnapshot.origin }),
        }
      : undefined;

  // The Selection logic graph — see useRuleSelectionGraph. The canvas never
  // reflects the unsaved editor buffer, and the caption says which saved
  // artifact is showing.
  const { graph: selectionGraph, isPublishedLoading: isPublishedGraphLoading } =
    useRuleSelectionGraph(ruleId);
  const canvasGraph = useOrgExclusionsGraph(selectionGraph);

  // The carts Atlas offers have to cover the rule's current branches; the
  // graph is what makes them stale, so the workspace is what keeps them fresh.
  useEnsureRuleScenarios({ ruleId, graph: selectionGraph });
  const scenarioPreview = useScenarioPreview({
    ruleId,
    graph: selectionGraph,
  });

  // The admin regenerate lane. The page owns the mutation pair so the
  // canvas can shimmer while a rebuild of an EXISTING graph is in flight —
  // the button and the skeleton share one busy signal.
  const regenerateGraph = useRegenerateGraph(ruleId);

  // The open Atlas session created this rule and is still authoring it: the
  // create tool navigated here before the first draft (and its graph) exist,
  // so publish/repair signals haven't started yet. Once the repair loop stops
  // — gave up, or was stood down by the sweep after its worker died —
  // `RepairGiveUpPanel` owns the story, and a canvas still shimmering "being
  // built" under it would be a lie.
  const atlasBuildingRuleId = useSelector(getAtlasBuildingRuleId);
  const isAtlasCreationRule = atlasBuildingRuleId === ruleId;
  const isAtlasCreationSessionActive = useSelector(
    getIsAtlasCreationSessionActive
  );
  const repairStopped = didRepairStop(vm.repair.run);
  const showRepairGiveUp = shouldShowRepairGiveUp(
    vm.repair.run,
    vm.repair.isVersionHistoryLoading
      ? undefined
      : (vm.activeVersion?.publishedAtTs ?? null)
  );
  const canRestoreRepairBaseline = canOfferRepairBaselineRestore(
    vm.repair.run,
    vm.activeVersion?.version ?? null
  );
  // A held run is terminal too: the checks went green, the rewrite is prepared,
  // and nothing is still working on the rule — so the "being built" shimmer
  // must stand down here exactly as it does for a stopped run, and
  // `RepairApprovalPanel` owns the story instead.
  const repairAwaitingApproval =
    isRepairAwaitingApproval(vm.repair.run) &&
    (rule.state === "draft" || hasUnpublishedChanges);
  const isAtlasBuildingThisRule =
    isAtlasCreationRule && !repairStopped && !repairAwaitingApproval;

  const isGraphPending =
    selectionGraph === null &&
    (vm.draft !== null ||
      isAtlasBuildingThisRule ||
      vm.publish.isPublishing ||
      vm.repair.isActive);

  // Atlas owns the source while its chat session is building this rule or its
  // repair loop is mid-fix: each saves a new draft over whatever is in the
  // editor, so the Advanced tab holds as "Atlas is updating…" until it is
  // done rather than inviting an edit the next save would overwrite.
  const isAtlasUpdatingSource = isAtlasBuildingThisRule || vm.repair.isActive;

  // The graph lands out-of-band — the chat's backend save, the repair loop's
  // regenerations, the SQS regen worker — none of which run through this
  // browser's mutations. While the canvas shows "being built", poll the draft
  // so the skeleton actually resolves.
  useDraftGraphPoll(ruleId, isGraphPending);

  // A suite gate opens its suite, which is a file in the Advanced editor; the
  // other gates have no destination beyond the tab itself.
  const openGateInEditor = (gateKey: AgenticLogicPublishGateKey) => {
    if (gateKey === AGENTIC_GATES_CONFIG.consoleGates.generatedSuite) {
      vm.editor.setActiveFile(AGENTIC_TEST_FILES.generatedFilename);
    } else if (gateKey === AGENTIC_GATES_CONFIG.consoleGates.userSuite) {
      vm.editor.setActiveFile(AGENTIC_TEST_FILES.userFilename);
    }
  };

  // The checks strip heads the Advanced tab, but the page may be on another
  // tab when a check is opened, so this crosses to Advanced first. Withheld
  // entirely when the org cannot see that tab: the strip draws its per-check
  // "Open" button from the presence of this callback, and without the
  // destination the button is a dead click rather than a disabled one.
  const handleSelectGate = advancedEnabled
    ? (gateKey: AgenticLogicPublishGateKey) => {
        setSelectedPageTab("advanced");
        openGateInEditor(gateKey);
      }
    : undefined;

  const config = buildAgenticDomainConfig({
    dataSurface,
    publishReview: agenticPublishReviewConfig({
      availableSecretNames: vm.availableSecretNames,
      dependencies: vm.dependencies,
      activeVersion: vm.activeVersion,
    }),
  });

  const authorName = config.chrome.aiAuthorName ?? "Atlas";

  // "Ask Atlas to fix" runs the checks again. That is the whole mechanism:
  // a failed run opens a repair run in the publish worker's terminal tail,
  // and the supervisor takes it from there.
  //
  // It used to be a tab switch and nothing else — no endpoint, no run — and
  // the chip appeared to "remove itself" because the gate rail that draws it
  // is not on the tab it switched to.
  const askAtlasToFix = vm.runTests;
  // ONE rule for both recovery affordances: they now dispatch the same action,
  // so they must be live under the same conditions.
  const operationBusy =
    vm.publish.isPublishing || vm.repair.isActive || vm.publishOutcomePending;
  const recheckDisabled = isDirty || operationBusy;
  const cancelChecks = useCancelRuleChecks({
    ruleId,
    publishJob: vm.publishJob,
    repair: vm.repair,
  });

  // The gate strip describes whichever publish job the port tracks, which is
  // not the rule's own state: a rule the repair loop stopped on never shipped,
  // yet the last run beside it can still read "All N checks passed". Hand the
  // strip the contradiction so it reports the run as stopped instead of a
  // verdict — the give-up panel renders directly above it.
  // Same lifetime as the panel: a stopped run that a later publish has
  // superseded is no longer a contradiction, and neither is one the version
  // history has not yet been able to judge.
  const gateRunAbandonedReason = showRepairGiveUp
    ? repairStoppedGateReason(vm.repair.run, authorName)
    : null;

  // The rail now shows the run that shipped a live rule, not only a draft's
  // pending checks. Beside such a rule "ready to publish" describes a publish
  // that already happened, so the strip reports the run as history instead.
  const publishedCurrentDraft = publishedJobCoversCurrentDraft({
    job: vm.publishJob,
    isDirty,
    draftLastUpdateTs: vm.draft?.lastUpdateTs,
    ruleLastUpdateTs: rule.lastUpdateTs,
  });
  const gateRunDescribesLiveVersion =
    publishedCurrentDraft ||
    (rule.state !== "draft" &&
      !hasUnpublishedChanges &&
      !isChecking &&
      !comparisonFailed &&
      !isDirty &&
      vm.publish.job?.status === "succeeded");

  // The rail follows the repair loop's CURRENT job, so every attempt it has
  // already burned leaves the screen the instant the next one is enqueued —
  // and a run that converges takes its refusals with it. These rows are the
  // record: the refusing gate and its remediation, verbatim.
  const attemptsRefusedByRepair = priorRepairAttempts(
    vm.repair.run,
    vm.publish.job?.id
  );

  const checkPhase = ruleCheckPhase({
    job: vm.publishJob,
    repairRun: vm.repair.run,
    busy: operationBusy,
    held: repairAwaitingApproval,
    stopped: showRepairGiveUp,
    hasDraftChanges: hasUnpublishedChanges || isDirty,
    checksStale:
      isDirty ||
      (vm.publishJob?.finishedAtTs != null &&
        (vm.draft?.lastUpdateTs ?? 0) > vm.publishJob.finishedAtTs),
    describesPublishedVersion: gateRunDescribesLiveVersion,
  });
  const checksOnly = isChecksOnlyRun({
    repairActive: vm.repair.isActive,
    repairPublishesOnSuccess: vm.repair.run?.publishOnSuccess,
    jobMode: vm.publishJob?.mode,
  });
  const operationPhase: RuleOperationPhase =
    isAtlasBuildingThisRule && vm.publishJob?.status !== "cancelled"
      ? "building"
      : operationBusy
        ? checksOnly
          ? "checking"
          : "applying"
        : "idle";
  // Nothing else must be persisted first: the rule already exists by the time
  // this route can be reached, and a rule is writable for its whole life —
  // retiring one deletes it rather than freezing it.
  const disablePersistedActions =
    vm.repair.isActive || vm.publishOutcomePending;

  // ONE judgement for whether the pending edits can be written, shared by the
  // autosave and the status line that explains a blocked one.
  const saveBlockReason = ruleSaveBlockReason({
    settingsConflict,
    isDirty,
    hasUnpublishedChanges,
    isChecking,
    comparisonFailed,
    isCodeDirty: vm.isDirty,
    saveAction,
    isReadOnly: disablePersistedActions,
    isSavingDraft: vm.isSavingDraft || saveSettings.isSaving,
    isPublishing: vm.publish.isPublishing,
    isSyntaxValid: vm.editor.syntax.isValid,
    isSyntaxPending: vm.editor.syntaxPending,
  });

  // "Saving" and a pending syntax verdict resolve on their own; the rest need
  // the merchant, so the status line names them.
  const saveBlockResolvesItself =
    saveBlockReason === null ||
    saveBlockReason === "saving" ||
    saveBlockReason === "syntax_checking";

  // Every edit autosaves: settings onto the rule row, code as the draft.
  const saveRule = (onError: () => void) => {
    if (!isDirty || saveBlockReason !== null) {
      return;
    }
    void (async () => {
      if (settingsDirty) {
        await saveSettings.save(settings);
        setSettingsEdit((edit) =>
          edit && !sameRuleSettings(edit.value, settings)
            ? { ...edit, base: { ...settings } }
            : null
        );
      }
      if (vm.isDirty) {
        await new Promise<void>((resolve, reject) =>
          vm.saveDraft({ onSaved: resolve, onNotSaved: reject })
        );
      }
    })().catch(onError);
  };

  const flow = useRuleSaveApplyFlow({
    rule,
    ruleName: vm.entityName,
    usedIn: byline.usedIn,
    comparison,
    edits: {
      isDirty,
      isSaving: vm.isSavingDraft || saveSettings.isSaving,
      canSave: saveBlockReason === null,
      canSaveOnLeave: saveBlockResolvesItself,
      saveBlockedHint: saveBlockResolvesItself
        ? undefined
        : RULE_SAVE_BLOCK_HINT[saveBlockReason],
      revision: [vm.draftSnapshot.files, vm.userTestSuite, settings],
      save: saveRule,
      savedAtTs:
        Math.max(vm.draft?.lastUpdateTs ?? 0, rule.lastUpdateTs) || null,
    },
    phase: operationPhase,
    apply: vm.publishDraft,
    publishJob: vm.publishJob,
    copyDraft,
    copyDisabledHint:
      disablePersistedActions || operationBusy
        ? BUSY_HINT
        : copyDraft === undefined
          ? "This rule has no saved code to copy yet."
          : undefined,
    runChecks: AGENTIC_CAPABILITIES.canRunGates
      ? {
          disabledHint:
            disablePersistedActions || operationBusy ? BUSY_HINT : undefined,
          run: vm.runTests,
        }
      : null,
  });
  const publishDisabledHint = flow.state.applyDisabledHint;
  let checkDetail: string | undefined;
  if (
    checkPhase === "failed" ||
    checkPhase === "review" ||
    checkPhase === "cancelled"
  ) {
    checkDetail =
      rule.state === "draft"
        ? "This rule has no applied changes yet."
        : "Your applied rule is unchanged.";
  }
  const stoppedRepairProps =
    checkPhase === "failed" && showRepairGiveUp && vm.repair.run
      ? {
          embedded: true,
          disabled: recheckDisabled,
          ruleId,
          run: vm.repair.run,
          authorName,
          advancedEnabled,
          canRestoreBaseline: canRestoreRepairBaseline,
          dataSurface,
          currentRuleContext: JSON.stringify(
            {
              name: rule.name,
              description: rule.description,
              settings,
              workspace: vm.draftSnapshot.files,
            },
            null,
            2
          ),
          failedChecks: JSON.stringify(
            vm.publish.gates
              .filter((gate) => gate.status === "failed")
              .map(({ gateKey, result, detailsJson }) => ({
                gateKey,
                result,
                details: detailsJson,
              })),
            null,
            2
          ),
          onOpenCodeWorkspace: () => setSelectedPageTab("advanced"),
        }
      : undefined;
  const checkOperation: GateStripOperation = {
    phase: checkPhase,
    checksOnly,
    detail: checkDetail,
    actions: stoppedRepairProps ? (
      <RepairGiveUpPanel {...stoppedRepairProps} display="actions" />
    ) : undefined,
    content:
      checkPhase === "review" && vm.repair.run ? (
        <RepairApprovalPanel
          embedded
          hasPublishedVersion={rule.state !== "draft"}
          ruleId={ruleId}
          run={vm.repair.run}
          authorName={authorName}
          advancedEnabled={advancedEnabled}
          isPublishing={operationBusy}
          isPublishDisabled={publishDisabledHint !== undefined}
          onPublish={flow.applyNow}
          onOpenCodeWorkspace={() => setSelectedPageTab("advanced")}
        />
      ) : stoppedRepairProps ? (
        <RepairGiveUpPanel {...stoppedRepairProps} display="details" />
      ) : undefined,
  };

  const [checksExpanded, setChecksExpanded] = useImmer<boolean | undefined>(
    undefined
  );
  // Placed once below the main tabs by RuleDetailPageView, above any inner
  // Advanced workspace tabs, so its position does not change across views.
  const checksStrip =
    vm.publish.job != null ||
    hasUnpublishedChanges ||
    vm.repair.run !== null ? (
      <GateProgressStrip
        gates={vm.publish.gates}
        config={config.gates}
        heading={config.chrome.gatesHeading}
        isPublishing={operationBusy}
        operation={checkOperation}
        topMargin={4}
        // RuleDetailPageView supplies the 16px side inset.
        horizontalMargin={0}
        bottomMargin={0}
        // One View / Hide checks state for the strip shared by every tab.
        expanded={checksExpanded}
        onExpandedChange={setChecksExpanded}
        priorAttempts={attemptsRefusedByRepair}
        onSelectGate={handleSelectGate}
        cancel={cancelChecks}
      />
    ) : null;

  return (
    <>
      {flow.dialogs}
      <RuleDetailPageView
        isBuilding={isAtlasCreationRule && isAtlasCreationSessionActive}
        revealBuilt={isAtlasCreationRule}
        backLabel={surface.backLabel}
        name={vm.entityName}
        description={vm.ruleDescription ?? ""}
        eligibleSurfaces={byline.eligibleSurfaces}
        usedIn={byline.usedIn}
        isRenaming={updateRule.isPending}
        isSavingDescription={updateRule.isPending}
        selectedTab={selectedPageTab}
        onSelectedTabChange={setSelectedPageTab}
        advancedEnabled={advancedEnabled}
        onRename={async (name) => {
          await updateRule.mutateAsync({
            body: { name },
            pathParams: { ruleId },
            queryParams: undefined,
          });
        }}
        onSaveDescription={async (description) => {
          await updateRule.mutateAsync({
            body: { description },
            pathParams: { ruleId },
            queryParams: undefined,
          });
        }}
        onBack={flow.goBack}
        headerStatus={flow.status}
        banners={
          <>
            {settingsConflict ? (
              <Box sx={{ px: 2, pt: 2 }}>
                <IgAlert
                  alertKey="rule-settings-conflict"
                  severity="warning"
                  title="The saved settings changed"
                  message="Your local settings have not been saved. Reload the saved settings before editing again."
                  primaryButton={{
                    cta: "Reload settings",
                    onClick: () => setSettingsEdit(null),
                  }}
                />
              </Box>
            ) : null}
          </>
        }
        actions={flow.actions}
        selectionLogic={
          <>
            <SelectionLogicTab
              graph={
                isAtlasCreationRule && isAtlasCreationSessionActive
                  ? null
                  : canvasGraph
              }
              isLoading={vm.isLoading || isPublishedGraphLoading}
              // A save with no graph regenerates one asynchronously, and that
              // worker runs alongside the publish. Until the publish settles, a
              // missing graph means "not built yet", not "this rule has none".
              // The repair loop keeps regenerating graph + source after a
              // failed publish, so "publishing settled" no longer means "the
              // graph is final" — pending tracks the loop too, and the open
              // Atlas session that is still authoring this rule's first draft.
              isPending={
                isGraphPending ||
                (isAtlasCreationRule && isAtlasCreationSessionActive)
              }
              revealBuilt={isAtlasCreationRule && !isAtlasCreationSessionActive}
              pendingHint={
                isAtlasBuildingThisRule && !vm.publish.isPublishing
                  ? "Atlas is writing this rule now — the graph appears when its first draft is saved."
                  : undefined
              }
              // Deliberately NOT tied to the repair loop: a repair run can
              // regenerate for minutes on a slow poll, and minutes of
              // shimmer reads as broken. Only the direct regenerate lane,
              // whose round trip is one request, shimmers the canvas.
              isRebuilding={regenerateGraph.isBusy && selectionGraph !== null}
              canvasAction={
                <RegenerateGraphButton
                  canRegenerate={regenerateGraph.canRegenerate}
                  isBusy={regenerateGraph.isBusy}
                  onRegenerate={regenerateGraph.regenerate}
                />
              }
              overlaysByNodeId={scenarioPreview.overlaysByNodeId}
              overlaysByEdgeId={scenarioPreview.overlaysByEdgeId}
            />
          </>
        }
        // Manual settings are included in Save draft; publishing remains separate.
        displaySettings={
          <Stack spacing={8} alignItems="flex-start">
            <RuleSettingsFields
              settings={settings}
              saving={saveSettings.isSaving || vm.publish.isPublishing}
              onChange={(value) =>
                setSettingsEdit((edit) => ({
                  base: edit?.base ?? { ...rule.settings },
                  value: { ...value },
                }))
              }
            />
            {/* The code runtime is expert material, so it shows where the
                code does: only to orgs with the Advanced tab. */}
            {advancedEnabled ? (
              <Stack spacing={2} sx={{ width: 468 }}>
                <IgTypography fontWeight={600}>Runtime</IgTypography>
                <RuntimeFactsList facts={config.controlsPanel.runtimeFacts} />
              </Stack>
            ) : null}
          </Stack>
        }
        checks={checksStrip}
        advanced={
          <CodegenWorkspace
            config={config}
            vm={vm}
            capabilities={AGENTIC_CAPABILITIES}
            // The page frame above owns the name, the description and the write
            // actions, so the shell must not draw a second header of its own.
            chrome="embedded"
            showBottomSection={false}
            showGateProgress={false}
            recentRuns={
              isAdmin ? <RecentRunsPanel ruleId={ruleId} /> : undefined
            }
            readOnly={false}
            isAiUpdating={isAtlasUpdatingSource}
            disablePersistedActions={disablePersistedActions}
            onSelectGate={openGateInEditor}
            onAskAiFix={recheckDisabled ? undefined : askAtlasToFix}
            onClose={flow.goBack}
            rollbackImpactCopy={agenticRollbackImpactCopy}
            gateRunAbandonedReason={gateRunAbandonedReason}
            gateRunDescribesLiveVersion={gateRunDescribesLiveVersion}
            // Version history has no tab on the rule page: edits autosave and
            // Discard is the way back to the live version.
            showVersionHistory={false}
          />
        }
      />
    </>
  );
};

export const AgenticLogicRuleWorkspacePage = ({
  rule,
}: {
  rule: AgenticLogicRuleDto;
}) => {
  const { settled, dataSurface } = useAgenticLogicDataSurface();

  // Gate the mount on the data surface being SETTLED, not on it succeeding.
  // An admin's types module is built from the org's dataset keys, so mounting
  // before they land would type `sdk.getDataset()` against an empty union and
  // squiggle an existing rule's calls until the query returned. Gating on
  // success instead would let a failed secondary metadata read block all code
  // editing, which is the worse failure — so on error we mount with the
  // zero-key projection and say so in a banner. A merchant settles as soon as
  // their admin status is known: their projection has no dataset surface.
  if (!settled) {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{ height: "100%" }}
      >
        <IgLoading />
      </Stack>
    );
  }

  return (
    // Keyed on ruleId so navigating between rules REMOUNTS the workspace. The
    // shell holds the editor buffer in local state and does not reset it when
    // the entity ref changes, so without this key rule A's unsaved code stays
    // in the buffer and the next save writes it over rule B's draft.
    <RuleWorkspace key={rule.id} rule={rule} dataSurface={dataSurface} />
  );
};

/** Shown while the route resolves without a rule id (should not happen). */
export const AgenticLogicRuleWorkspaceFallback = () => (
  <IgTypography>Rule not found.</IgTypography>
);
